<template>
  <el-dialog
    v-model="shouldShow"
    title="Nova alteração de preço"
    width="80%"
    @closed="resetForm"
  >
    <el-form v-if="!isViewMode" label-position="top">
      <el-row :gutter="16">
        <el-col :md="8" :xs="24">
          <el-form-item label="Percentual de alteração (%)">
            <el-input
              v-model.number="form.amount"
              type="number"
              :min="-100"
              :max="100"
              placeholder="Percentual entre -100 e 100"
              clearable
            >
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :md="8" :xs="24">
          <el-form-item label="Categorias">
            <el-select
              v-model="form.categories"
              multiple
              filterable
              placeholder="Selecione categorias"
            >
              <el-option
                v-for="category in categories"
                :key="category.uid"
                :label="category.name"
                :value="category.uid"
              ></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :md="8" :xs="24">
          <el-form-item label="Produtos específicos">
            <el-select
              v-model="form.products"
              multiple
              filterable
              placeholder="Selecione produtos"
            >
              <el-option
                v-for="product in productOptions"
                :key="product.uid"
                :label="productLabel(product)"
                :value="product.uid"
              ></el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <el-row type="flex" justify="start">
      <h4>Pré-visualização</h4>
    </el-row>
    <el-table
      stripe
      :cell-style="() => 'text-align:center;'"
      :data="mountedProducts"
      v-loading="isLoadingPreview"
      style="width: 100%; z-index: 0"
    >
      <el-table-column label="produto">
        <template #default="scope">
          {{ scope.row.name }}
        </template>
      </el-table-column>
      <el-table-column v-if="!isViewMode" label="preço atual">
        <template #default="scope">
          {{ formatCurrency(scope.row.currentPrice) }}
        </template>
      </el-table-column>
      <el-table-column v-if="!isViewMode" label="novo preço">
        <template #default="scope">
          {{ formatCurrency(newPrice(scope.row.currentPrice)) }}
        </template>
      </el-table-column>
    </el-table>

    <template #footer>
      <el-button size="medium" @click="shouldShow = false">
        Fechar
      </el-button>
      <el-button
        v-if="!isViewMode"
        type="primary"
        size="medium"
        :disabled="isSaving || !canSave"
        @click="savePriceChange"
      >
        Salvar
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import ProductPriceChangeService from "../../services/product_price_changes";
import CategoryService from "../../services/categories";
import ProductService from "../../services/products";
import { currencyFormatter } from "../../utils/formatters";
import { notifyError, notifySuccess } from "../../utils/notifiers";

export default {
  name: "ProductPriceChangeModal",
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create",
    },
    viewMountedProducts: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    isSaving: false,
    isLoadingPreview: false,
    categories: [],
    previewProducts: [],
    form: {
      amount: null,
      categories: [],
      products: [],
    },
  }),
  computed: {
    isViewMode() {
      return this.mode === "view";
    },
    shouldShow: {
      get() {
        return this.showModal;
      },
      set() {
        this.$emit("close-modal");
      },
    },
    productOptions() {
      const map = new Map();
      (this.categories || []).forEach((category) => {
        (category.products || []).forEach((product) => {
          if (!map.has(product.uid)) {
            map.set(product.uid, {
              ...product,
              categoryName: category.name,
            });
          }
        });
      });
      return Array.from(map.values());
    },
    selectedCategories() {
      return (this.categories || []).filter((category) =>
        this.form.categories.includes(category.uid)
      );
    },
    selectedProducts() {
      return this.productOptions.filter((product) =>
        this.form.products.includes(product.uid)
      );
    },
    mountedProducts() {
      if (this.isViewMode) {
        return (this.viewMountedProducts || []).map((mounted) => {
          const name = [
            mounted?.product?.name,
            mounted?.cutting,
            mounted?.composition,
            mounted?.storage,
          ]
            .filter(Boolean)
            .join(" ");
          return {
            uid: mounted?.uid,
            name,
            currentPrice: Number(mounted?.price?.female_std || 0),
          };
        });
      }

      const map = new Map();
      const addMountedProducts = (product) => {
        (product.mounted || []).forEach((mounted) => {
          if (!mounted?.id || map.has(mounted.id)) return;
          const name = [
            product.name,
            mounted.cutting,
            mounted.composition,
            mounted.storage,
          ]
            .filter(Boolean)
            .join(" ");
          map.set(mounted.id, {
            uid: mounted.id,
            name,
            currentPrice: Number(mounted?.price?.female_std || 0),
          });
        });
      };

      (this.previewProducts || []).forEach(addMountedProducts);

      return Array.from(map.values());
    },
    isAmountValid() {
      if (this.form.amount === null || this.form.amount === "") return false;
      return this.form.amount >= -100 && this.form.amount <= 100;
    },
    canSave() {
      return this.isAmountValid && this.mountedProducts.length > 0;
    },
  },
  watch: {
    showModal(value) {
      if (value && !this.isViewMode) this.loadCategories();
    },
    "form.categories": {
      handler() {
        if (!this.isViewMode) this.fetchPreviewProducts();
      },
      deep: true,
    },
    "form.products": {
      handler() {
        if (!this.isViewMode) this.fetchPreviewProducts();
      },
      deep: true,
    },
  },
  methods: {
    formatCurrency(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value)))
        return "-";
      return currencyFormatter.format(Number(value));
    },
    newPrice(currentPrice) {
      const amount = Number(this.form.amount) || 0;
      const price = Number(currentPrice || 0);
      return Math.round(price * (1 + amount / 100));
    },
    productLabel(product) {
      return product.categoryName
        ? `${product.name} - ${product.categoryName}`
        : product.name;
    },
    resetForm() {
      this.form = {
        amount: null,
        categories: [],
        products: [],
      };
      this.previewProducts = [];
    },
    async loadCategories() {
      if (this.categories?.length) return;
      const { categories, error } = await CategoryService().index();
      if (error) {
        notifyError();
        return;
      }
      this.categories = categories || [];
    },
    async fetchPreviewProducts() {
      const categoryIds = [...new Set(this.form.categories || [])];
      const productIds = [...new Set(this.form.products || [])];

      if (!categoryIds.length && !productIds.length) {
        this.previewProducts = [];
        return;
      }

      this.isLoadingPreview = true;

      try {
        const categoryRequests = categoryIds.map((uid) =>
          ProductService().index({ category_id: uid })
        );
        const productRequests = productIds.map((uid) =>
          ProductService(uid).get()
        );
        const results = await Promise.all([
          ...categoryRequests,
          ...productRequests,
        ]);

        const productsMap = new Map();
        results.forEach((result) => {
          if (result?.products) {
            result.products.forEach((product) => {
              productsMap.set(product.uid, product);
            });
          }

          if(result?.product){
            productsMap.set(result.product.uid, result.product)
          }
        });

        this.previewProducts = Array.from(productsMap.values());
      } catch (error) {
        notifyError();
      } finally {
        this.isLoadingPreview = false;
      }
    },
    async savePriceChange() {
      if (!this.isAmountValid) {
        notifyError(
          "Percentual inválido",
          "Informe um percentual entre -100 e 100"
        );
        return;
      }
      if (!this.mountedProducts.length) {
        notifyError(
          "Seleção inválida",
          "Selecione ao menos um produto para alterar o preço"
        );
        return;
      }

      this.isSaving = true;
      const payload = {
        amount: Number(this.form.amount),
        products: this.mountedProducts.map((item) => item.uid),
      };
      const { productPriceChange, error } =
        await ProductPriceChangeService().create(payload);
      this.isSaving = false;

      if (error || !productPriceChange) {
        notifyError();
        return;
      }

      notifySuccess("Alteração criada com sucesso");
      this.$emit("close-modal");
      this.$emit("saved");
    },
  },
};
</script>
